import { RenderCellProps } from '@/components/cashflow/transactions/TableRow';
import useUpdateTransactionEntry from '@/components/cashflow/useUpdateTransactionEntry';
import InputField from '@/components/common/InputField';
import { groupHoverStyleProps } from '@/components/common/styleHelpers';
import { useErrorToast } from '@/components/common/Toasts';
import { Paragraph, Text } from '@/components/common/Typography';
import { Box } from '@chakra-ui/react';
import { TOASTS } from '@common/toasts';
import { DT, dt } from '@common/util/dateTime';
import React, { useState } from 'react';

const DateCell = ({ transactionEntry, query, dateRange }: RenderCellProps) => {
    const isShowingMultipleYears =
        dt(dateRange.startDate).year() !== dt(dateRange.endDate).year();
    const showErrorToast = useErrorToast();

    const [date, setDate] = useState<DT>(dt(transactionEntry.date));
    const [isEditing, setEditing] = useState<boolean>(false);

    const updateTransactionEntry = useUpdateTransactionEntry(transactionEntry);

    const dateStr =
        isShowingMultipleYears || query
            ? date.format('MMM D, YYYY')
            : date.format('MMM D');

    if (transactionEntry.isPending) {
        return (
            <Paragraph ml="10px" mt="3px" opacity={0.5}>
                {dateStr}
            </Paragraph>
        );
    }

    return isEditing ? (
        // TODO: The dialog gets cut off if the table is small - need to figure out portal issues
        <InputField
            name="transaction-date"
            type="date"
            dateOptions={{
                isOpen: true,
                onOpenChange: (isOpen) => setEditing(isOpen),
            }}
            inputProps={{
                variant: 'outline',
                size: 'md',
            }}
            mt={-2.5}
            w="90%"
            value={date.toDateStr()}
            onChange={async (v) => {
                const oldDate = date;
                const newDate = dt(v);
                setDate(newDate);

                try {
                    await updateTransactionEntry({
                        variables: {
                            transactionEntryId: transactionEntry.id,
                            date: newDate.toDateStr(),
                        },
                    });
                } catch {
                    showErrorToast(TOASTS.updateTransactionEntryDateError);
                    setDate(oldDate);
                }

                setEditing(false);
            }}
        />
    ) : (
        <Box
            py="11px"
            px={2}
            mr={4}
            mt={-2}
            borderRadius={6}
            border="1px solid transparent"
            {...groupHoverStyleProps({
                borderColor: 'gray.300',
            })}
            onClick={(e) => {
                e.stopPropagation();
                setEditing(true);
            }}
        >
            <Text overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">
                {dateStr}
            </Text>
        </Box>
    );
};

export default DateCell;
